import {Banner} from '@primer/react'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {
  requestCopilotCodingAgentAccess,
  removeCopilotCodingAgentRequest,
  enableCopilotCodingAgentPolicy,
} from '@github-ui/copilot-member-feature-requests'
import {useClickAnalytics} from '@github-ui/use-analytics'
import React, {useCallback, useState} from 'react'

import styles from './MemberRequestBanner.module.css'

export const MemberRequestBanner = React.memo(MemberRequestBannerUnmemoized)

function MemberRequestBannerUnmemoized({
  repo,
  onRepoUpdate,
}: {
  repo: DropdownRepository
  onRepoUpdate: () => Promise<void>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  // Extract org from nameWithOwner (format: "owner/repo")
  const orgLogin = repo.owner

  const handleRequestAccess = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    sendClickAnalyticsEvent({
      category: 'member_feature_request',
      action: 'action.copilot_coding_agent',
      label: 'ref_cta:ask_admin_for_access;ref_loc:agents_new_task_banner;',
    })

    try {
      await requestCopilotCodingAgentAccess(orgLogin)
      // Request created successfully - refresh the repository data
      await onRepoUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, sendClickAnalyticsEvent, onRepoUpdate])

  const handleRemoveRequest = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await removeCopilotCodingAgentRequest(orgLogin)
      // Request cancelled successfully - refresh the repository data
      await onRepoUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, onRepoUpdate])

  const handleEnableFeature = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await enableCopilotCodingAgentPolicy(orgLogin)
      // Feature enabled successfully - refresh the repository data
      await onRepoUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, onRepoUpdate])

  let bannerText = ''
  let buttonText = ''
  let onClick = () => {}

  if (repo.userCanRequestCca) {
    bannerText = 'Copilot coding agent access is disabled by your enterprise.'
    buttonText = isSubmitting ? 'Requesting...' : 'Request access'
    onClick = handleRequestAccess
  } else if (repo.userCanGrantEnterpriseCca) {
    bannerText = 'Copilot coding agent access is disabled.'
    buttonText = 'Set Copilot policies'
    onClick = () => {
      const enterpriseSlug = repo.businessSlug || orgLogin
      window.location.href = `/enterprises/${encodeURIComponent(enterpriseSlug)}/ai-controls/copilot`
    }
  } else if (repo.userCanGrantOrgCca) {
    bannerText = 'Copilot coding agent access is disabled.'
    buttonText = isSubmitting ? 'Enabling...' : 'Enable feature'
    onClick = handleEnableFeature
  } else if (repo.userHasRequestedCca) {
    bannerText = 'Copilot coding agent access is disabled by your enterprise.'
    buttonText = isSubmitting ? 'Removing...' : 'Remove request'
    onClick = handleRemoveRequest
  } else {
    return null
  }

  return (
    <>
      <Banner
        className={styles.requestBanner}
        variant="warning"
        title="Coding Agent Disabled"
        hideTitle
        description={bannerText}
        primaryAction={
          <Banner.PrimaryAction onClick={onClick} disabled={isSubmitting}>
            {buttonText}
          </Banner.PrimaryAction>
        }
      />
      {error && (
        <div aria-live="assertive">
          <Banner variant="critical" title="Error" hideTitle description={error} className={styles.requestBanner} />
        </div>
      )}
    </>
  )
}

try{ MemberRequestBanner.displayName ||= 'MemberRequestBanner' } catch {}
try{ MemberRequestBannerUnmemoized.displayName ||= 'MemberRequestBannerUnmemoized' } catch {}